import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react'
import type { ClusterHealthEvent, ClusterHealthSummary } from './types'
import { fetchHealthEvents, fetchHealthSummary } from './useAdminApi'
import { AdminTable, Banner, SectionHeader, Td, Th } from './ui'
import { pollWhileVisible } from '../lib/pollWhileVisible'

const POLL_MS = 15_000

const SEVERITY_PILL: Record<string, string> = {
  CRITICAL: 'bg-rose-50 text-rose-700 border-rose-200',
  WARN: 'bg-amber-50 text-amber-700 border-amber-200',
  INFO: 'bg-slate-100 text-slate-600 border-slate-200',
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) return 'just now'
  const mins = Math.floor(ms / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

/** Super-admin only -- enforced server-side too (403 on both /api/admin/health routes for
 *  anyone else), same gate UsersSection/AccessSection already use. Reads
 *  cluster_health_event (db/schema_health.sql), written by tools/check_cluster_health.py's
 *  CronJob: pod restarts/crash-loops/OOM kills across the whole cluster, not just the one
 *  Nagpur-specific staleness signal that existed before it. This is the source of truth --
 *  Teams notification is best-effort on top, not instead of, per that table's own comment
 *  on why (a 200 from the webhook never proved anyone saw the message). */
export function HealthSection({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [events, setEvents] = useState<ClusterHealthEvent[]>([])
  const [summary, setSummary] = useState<ClusterHealthSummary | null>(null)
  const [showResolved, setShowResolved] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null)

  const load = useCallback(async (openOnly: boolean) => {
    setError(null)
    try {
      const [ev, sm] = await Promise.all([fetchHealthEvents(openOnly), fetchHealthSummary()])
      setEvents(ev)
      setSummary(sm)
      setLastRefreshed(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load cluster health')
    } finally {
      setLoading(false)
    }
  }, [])

  // Polling, not a single load -- this page exists so a real issue surfaces without
  // anyone having to come back and press refresh, the exact gap the 2026-10-08 Nagpur
  // restart loop fell into with only a Teams webhook nobody saw. One effect, not two --
  // pollWhileVisible's own immediate first tick covers what used to be a separate
  // mount-only load effect.
  useEffect(() => {
    setLoading(true)
    return pollWhileVisible(() => void load(!showResolved), POLL_MS)
  }, [load, showResolved])

  if (!isSuperAdmin) {
    return (
      <p className="text-sm text-slate-500">
        The cluster health dashboard is limited to the platform's super administrator.
      </p>
    )
  }

  const totalOpen = summary ? summary.open_critical + summary.open_warn + summary.open_info : 0

  return (
    <div className="space-y-4">
      <SectionHeader
        title="System health"
        subtitle="Pod restarts, crash-loops, OOM kills, and pipeline staleness across the cluster."
        action={
          <button
            type="button"
            onClick={() => void load(!showResolved)}
            className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw size={13} />
            Refresh
          </button>
        }
      />

      {error ? <Banner tone="error">{error}</Banner> : null}

      <div className="grid grid-cols-3 gap-3">
        <SummaryTile label="Critical" value={summary?.open_critical ?? 0} tone="rose" />
        <SummaryTile label="Warnings" value={summary?.open_warn ?? 0} tone="amber" />
        <SummaryTile label="Info" value={summary?.open_info ?? 0} tone="slate" />
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          <input
            type="checkbox" checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300"
          />
          Show resolved too
        </label>
        {lastRefreshed ? (
          <span className="text-xs text-slate-400">Updated {timeAgo(lastRefreshed.toISOString())}</span>
        ) : null}
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading…</p> : totalOpen === 0 && !showResolved ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700">
          <CheckCircle2 size={16} />
          No open issues right now.
        </div>
      ) : (
        <AdminTable>
          <thead>
            <tr>
              <Th>Severity</Th>
              <Th>Kind</Th>
              <Th>Pod / object</Th>
              <Th>Message</Th>
              <Th>Occurrences</Th>
              <Th>First seen</Th>
              <Th>Last seen</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {events.map((ev) => (
              <tr key={ev.id} className={ev.resolved_ts ? 'opacity-50' : 'hover:bg-slate-50/80'}>
                <Td>
                  <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium ${SEVERITY_PILL[ev.severity] ?? SEVERITY_PILL.INFO}`}>
                    {ev.severity === 'CRITICAL' ? <AlertTriangle size={11} /> : null}
                    {ev.severity}
                  </span>
                </Td>
                <Td mono>{ev.kind}</Td>
                <Td mono>{ev.pod ?? ev.namespace ?? ev.fleet ?? '—'}</Td>
                <Td className="max-w-[420px]"><span className="block truncate" title={ev.message}>{ev.message}</span></Td>
                <Td>{ev.occurrences}</Td>
                <Td>{timeAgo(ev.first_seen_ts)}</Td>
                <Td>{timeAgo(ev.last_seen_ts)}</Td>
                <Td>
                  {ev.resolved_ts ? (
                    <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs font-medium text-slate-600">resolved</span>
                  ) : (
                    <span className="rounded bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-700">open</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </AdminTable>
      )}
    </div>
  )
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone: 'rose' | 'amber' | 'slate' }) {
  const toneCls = {
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-600',
  }[tone]
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${toneCls}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  )
}
